import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Button, Header, RadioProps } from 'semantic-ui-react';
import styled from 'styled-components';
import DatePicker from 'components/final-form/DatePicker';
import { noop } from 'utils/functionUtils';
import { BillJournalData, BillJournalSearchRequest } from 'ts-types/api.types';
import Radio from 'components/final-form/Radio';
import CheckBox from 'components/final-form/CheckBox';
import { FormApi, FormState } from 'final-form';
import { BillJournalOrderBy } from 'ts-types/api.enums';
import moment from 'moment';
import { searchBillJournal } from 'services/billJournalServices';
import { BillJournalFormData } from 'components/billing/bill-journal/BillJournalView';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledContainer = styled.div`

   flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
      font-size: 1em !important;
    }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 0 1 auto;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterBillJournalData: (billJournalData: BillJournalData) => void,
  onBillsLoadingChange: (treatmentsLoading: boolean) => void,
  setOutputFormData: (outputFormData: BillJournalFormData) => void,
  setErrorMessage: (errorMessage?: string) => void
}

class BillJournalOptionsSegment extends Component<Props> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();
  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage} = props;

    const orderByValues = Object.values(BillJournalOrderBy);

    const orderBy = generateRadioButtonOptionsFromEnum(orderByValues, "orderBy", getMessage);

    orderBy.forEach((radioDefinition, index) => {
      radioMap.set(orderByValues[index], radioDefinition);
    });
  };

  initialSearchValues = (): Partial<BillJournalSearchRequest> => {

    const {currentUser} = this.props;

    const fromDate = moment().startOf('month').format("YYYY-MM-DD");
    const toDate = moment().endOf('month').format("YYYY-MM-DD");

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: fromDate,
      toDate: toDate,
      openBills: true,
      orderBy: BillJournalOrderBy.BILL_NUMBER
    }
  };

  handleChange = ({values}: FormState, form: FormApi) => {
    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }
  };

  searchBills = (values: any) => {

    const {
      onBillsLoadingChange,
      onFilterBillJournalData,
      setOutputFormData,
      setErrorMessage,
      getMessage
    } = this.props;

    this.setState({
      searchData: values
    });

    onBillsLoadingChange(true);

    searchBillJournal(values, this.cancelTokenSource)
        .then(response => {
          onFilterBillJournalData(response);

          const outputFormData: BillJournalFormData = {
            mandateId: values.mandateId,
            fromDate: values.fromDate,
            toDate: values.toDate,
            orderBy: values.orderBy,
            bills: response.bills
          }
          setOutputFormData(outputFormData);
        })
        .catch((error) => {
          setErrorMessage(getMessage("error.general"));
        })
        .finally(() => {
          onBillsLoadingChange(false);
        })
  };

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.searchBills(values)
    }
  };

  render(): React.ReactElement {
    return (
        <FinalForm
            onSubmit={() => noop}
            initialValues={this.initialSearchValues()}
            subscription={{values: true, pristine: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {getMessage} = props;

    const billNumber = radioMap.get(BillJournalOrderBy.BILL_NUMBER);
    const patientName = radioMap.get(BillJournalOrderBy.PATIENT_NAME);

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup className="noMargin">
                <Header size="tiny">{getMessage("billJournal.timePeriod")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("medicament.selection")}</Header>
                <SearchField>
                  <Field
                      name="openBills"
                      component={CheckBox}
                      label={getMessage('billJournal.openBills')}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="paidBills"
                      component={CheckBox}
                      label={getMessage('billJournal.paidBills')}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="stornoedBills"
                      component={CheckBox}
                      label={getMessage('billJournal.stornoedBills')}
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.orderBy")}</Header>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={billNumber}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={patientName}
                  />
                </SearchField>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => this.searchBills(values)}>
                  {getMessage("search")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  };

}

export default withAuthContext(withMessages(BillJournalOptionsSegment));
